import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Mail, Phone, Globe, User, Link2, Loader2, CalendarClock,
  StickyNote, PhoneCall, Users2, Handshake, HeartPulse, Route as RouteIcon, Ticket, FileText, MapPin,
  BookOpen, ChevronRight, Package, Pencil, Check, X, Plus,
  ShieldCheck,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import RequestApprovalDialog from "../components/dashboard/RequestApprovalDialog";
import NeedsAttention from "../components/dashboard/clients/NeedsAttention";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { formatDateMed } from "../lib/date";
import { ACTIVITY_TYPES, STATUS_ORDER, STATUS_META, CALL_OUTCOME_ORDER, CALL_OUTCOME_LABELS } from "../components/dashboard/prospects/prospectConstants";
import QuotesPanel from "../components/dashboard/quotes/QuotesPanel";
import ConvertToClientDialog from "../components/dashboard/prospects/ConvertToClientDialog";
import { ContactsEditor } from "../components/dashboard/ContactsEditor";
import ProspectRecordView from "./ProspectRecordView";
import { OverseasBadge } from "../components/dashboard/OverseasBadge";
import { EmailBody, Attachments, DirectionBadge } from "../components/dashboard/CapturedEmail";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABEL = { EA: "EQUIPR Allocate", EM: "EQUIPR Maintain", EO: "EQUIPR Operator", EC: "EQUIPR Core", EG: "EQUIPR Guardian", ER: "EQUIPR Remote", EH: "EQUIPR Horizon" };
const PRODUCT_CODES = ["EA", "EM", "EO", "EC", "EG", "ER", "EH"];
const toggleProduct = (list, code) => (list.includes(code) ? list.filter((x) => x !== code) : [...list, code]);

const KIND_META = {
  note: { label: "Note", icon: StickyNote, fg: "#6B5600", bg: "#FFF7D1" },
  email: { label: "Email", icon: Mail, fg: "#1D4ED8", bg: "#E3ECFF" },
  call: { label: "Call", icon: PhoneCall, fg: "#0F766E", bg: "#D6F2EE" },
  meeting: { label: "Meeting", icon: Users2, fg: "#7C3AED", bg: "#EDE4FF" },
  task: { label: "Task", icon: CalendarClock, fg: "#B45309", bg: "#FDEBD0" },
  health: { label: "Health check", icon: HeartPulse, fg: "#B02A6B", bg: "#FBE3EF" },
  linkedin: { label: "LinkedIn", icon: Link2, fg: "#0A66C2", bg: "#E3ECFF" },
  sms: { label: "SMS", icon: Mail, fg: "#0F766E", bg: "#D6F2EE" },
};
const FILTERS = [
  { key: "all", label: "All" },
  { key: "note", label: "Notes" },
  { key: "email", label: "Emails" },
  { key: "call", label: "Calls" },
  { key: "task", label: "Tasks" },
  { key: "meeting", label: "Meetings" },
];

function TimelineItem({ it }) {
  const meta = KIND_META[it.kind] || KIND_META.note;
  const Icon = meta.icon;
  const [expanded, setExpanded] = useState(false);
  const isEmail = it.kind === "email";
  const messages = it.messages || (isEmail ? [it] : null);
  const isThread = isEmail && messages && messages.length > 1;
  const hasEmailBody = isEmail && messages.some((m) => m.bodyHtml || (m.body && m.body.trim()));
  const longBody = it.body && it.body.length > 220;
  return (
    <div className="flex gap-3 pb-4" data-testid={`tl-item-${it.id}`}>
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0"
             style={{ background: "#F4F2ED", border: "1px solid #E6E2D9" }}>
          <Icon className="w-4 h-4" style={{ color: "#6B6660" }} />
        </div>
        <div className="w-px flex-1 bg-[#EDEAE3] mt-1" />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="mono text-[10px] uppercase tracking-[0.06em]" style={{ color: "#6B6660" }}>{meta.label}</span>
          <span className="text-sm font-medium text-[#1A1A1A]">{it.title}</span>
          {isThread && <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-[#E3ECFF] text-[#1D4ED8] border border-[#D5E1F6]" data-testid={`tl-thread-count-${it.id}`}>{it.count} messages</span>}
          <span className="text-xs text-[#8A857A] ml-auto mono">{it.date ? formatDateMed(it.date) : ""}</span>
        </div>
        {it.subtitle && !isEmail && <div className="text-xs text-[#59554D] mt-0.5">{it.subtitle}</div>}
        {isEmail ? (
          hasEmailBody && (
            <>
              <button onClick={() => setExpanded((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#1D4ED8] hover:underline mt-1" data-testid={`tl-toggle-${it.id}`}>
                {expanded ? "Hide messages" : (isThread ? `View ${it.count} messages` : "View email")}
                <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
              </button>
              {expanded && (
                <div className="mt-2 space-y-2" data-testid={`tl-thread-${it.id}`}>
                  {messages.map((m) => (
                    <div key={m.id} className="rounded-md border p-2.5"
                      style={m.direction === "inbound"
                        ? { borderColor: "#CFE3D6", background: "#F7FBF8" }
                        : { borderColor: "#EDEAE3", background: "#FBFAF7" }}
                      data-testid={`tl-email-msg-${m.id}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <DirectionBadge direction={m.direction} captured={m.captured}
                          testId={`tl-email-dir-${m.id}`} />
                        <span className="mono text-[10.5px] text-[#8A857A]">
                          {m.date ? formatDateMed(m.date) : ""}{m.owner ? ` · ${m.owner}` : ""}
                        </span>
                      </div>
                      <EmailBody html={m.bodyHtml} text={m.body} className="rv-email-body"
                        testId={`tl-email-body-${m.id}`} />
                      <Attachments items={m.attachments} testId={`tl-email-attachments-${m.id}`} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )
        ) : (
          it.body && (
            <div className="text-sm text-[#3A3630] mt-1">
              <div
                className="whitespace-pre-wrap break-words"
                style={longBody && !expanded ? { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}
              >
                {it.body}
              </div>
              {longBody && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="text-xs font-semibold text-[#1D4ED8] hover:underline mt-1"
                  data-testid={`tl-toggle-${it.id}`}
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function RailCard({ title, icon: Icon, count, action, children }) {
  return (
    <Card className="bg-white border-[#DAD5CA]">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#EDEAE3]">
          {Icon && <Icon className="w-4 h-4 text-[#59554D]" />}
          <span className="text-sm font-semibold text-[#1A1A1A]">{title}</span>
          {count != null && <Badge variant="outline" className="ml-auto border-[#DAD5CA] text-[#59554D] text-xs">{count}</Badge>}
          {action && <div className="ml-auto">{action}</div>}
        </div>
        <div className="p-3">{children}</div>
      </CardContent>
    </Card>
  );
}

export default function RecordView({ recordType, recordId }) {
  const isProspect = recordType === "prospect";
  if (isProspect) return <ProspectRecordView recordId={recordId} />;
  return <ClientRecordView recordType={recordType} recordId={recordId} />;
}

function ClientRecordView({ recordType, recordId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isProspect = recordType === "prospect";
  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [checks, setChecks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [staff, setStaff] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [runbook, setRunbook] = useState(null);
  const [runbookLoading, setRunbookLoading] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const emptyTouch = { type: "note", activity_date: new Date().toISOString().slice(0, 10), rep: "", outcome: "", notes: "", advance_status: "none", call_outcome: "none", callback_date: "" };
  const [touchOpen, setTouchOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [touch, setTouch] = useState(emptyTouch);
  const [loggingTouch, setLoggingTouch] = useState(false);

  const logTouch = async () => {
    setLoggingTouch(true);
    try {
      if (isProspect) {
        await axios.post(`${API}/sales/activities`, {
          prospect_id: recordId,
          type: touch.type,
          activity_date: touch.activity_date,
          rep: touch.rep || null,
          outcome: touch.outcome || null,
          notes: touch.notes || "",
          advance_status: touch.advance_status !== "none" ? touch.advance_status : null,
          call_outcome: touch.type === "call" && touch.call_outcome !== "none" ? touch.call_outcome : null,
          callback_date: touch.call_outcome === "callback_requested" ? (touch.callback_date || null) : null,
        });
      } else {
        await axios.post(`${API}/customer-success/clients/${encodeURIComponent(recordId)}/touches`, {
          type: touch.type, activity_date: touch.activity_date, rep: touch.rep || null,
          outcome: touch.outcome || null, notes: touch.notes || "",
        });
      }
      toast.success("Touch logged");
      setTouch(emptyTouch);
      setTouchOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log touch");
    } finally {
      setLoggingTouch(false);
    }
  };

  const openDetailsEdit = () => {
    setDetailsForm({
      company_name: rec.company_name || "",
      jira_org_name: rec.jira_org_name || "",
      allocate_version: rec.allocate_version || "",
      allocate_last_update: rec.allocate_last_update || "",
      products: rec.products || [],
      is_vip: !!rec.is_vip,
      has_sla: !!rec.has_sla,
      active: rec.active !== false,
    });
    setEditingDetails(true);
  };

  const saveDetailsEdit = async () => {
    setSavingDetails(true);
    try {
      await axios.put(`${API}/customer-success/clients/${encodeURIComponent(recordId)}`, {
        company_name: detailsForm.company_name || null,
        jira_org_name: detailsForm.jira_org_name || null,
        allocate_version: detailsForm.allocate_version || "",
        allocate_last_update: detailsForm.allocate_last_update || "",
        products: detailsForm.products,
        is_vip: detailsForm.is_vip,
        has_sla: detailsForm.has_sla,
        active: detailsForm.active,
      });
      setRec((prev) => ({ ...prev, ...detailsForm }));
      toast.success("Client details updated");
      setEditingDetails(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update client");
    } finally {
      setSavingDetails(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/agents/mapping`).then((r) => setStaff((r.data.agents || []).filter((a) => a.id !== "emergency"))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isProspect) {
        const [p, m] = await Promise.all([
          axios.get(`${API}/sales/prospects/${recordId}`),
          axios.get(`${API}/sales/prospects/${recordId}/meetings`).catch(() => ({ data: {} })),
        ]);
        const prospect = p.data.prospect;
        setRec(prospect);
        setContacts(prospect.contacts || []);
        setActivities(prospect.activities || []);
        setMeetings(m.data.meetings || []);
      } else {
        const cid = encodeURIComponent(recordId);
        const [cl, hc, mt, tc, ct] = await Promise.all([
          axios.get(`${API}/customer-success/clients/${cid}`),
          axios.get(`${API}/customer-success/health-checks`, { params: { client_id: recordId } }).catch(() => ({ data: {} })),
          axios.get(`${API}/customer-success/clients/${cid}/meetings`).catch(() => ({ data: {} })),
          axios.get(`${API}/customer-success/clients/${cid}/touches`).catch(() => ({ data: {} })),
          axios.get(`${API}/customer-success/clients/${cid}/contacts`).catch(() => ({ data: {} })),
        ]);
        const client = cl.data.client || { client_id: recordId, company_name: recordId };
        setRec(client);
        setChecks(hc.data.health_checks || []);
        setMeetings(mt.data.meetings || []);
        setActivities(tc.data.touches || tc.data.activities || []);
        setContacts(ct.data.contacts || []);
      }
    } catch (e) {
      toast.error("Failed to load record");
    } finally {
      setLoading(false);
    }
  }, [isProspect, recordId]);

  useEffect(() => { load(); }, [load]);

  // Auto-open the "Log a touch" dialog when arriving from the list action
  useEffect(() => {
    if (searchParams.get("action") === "log-touch") {
      setTouchOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Client runbook summary (full-page record is fully functional, not just a view)
  useEffect(() => {
    if (isProspect || !recordId) { setRunbook(null); return; }
    setRunbookLoading(true);
    axios.get(`${API}/runbooks/${encodeURIComponent(recordId)}`)
      .then((r) => setRunbook(r.data))
      .catch(() => setRunbook(null))
      .finally(() => setRunbookLoading(false));
  }, [isProspect, recordId]);

  const timeline = useMemo(() => {
    const norm = (s) => (s || "").replace(/^\s*(re|fwd?)\s*:\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();
    const items = [];
    activities.forEach((a, i) => {
      const t = (a.type || "note").toLowerCase();
      const kind = KIND_META[t] ? t : "note";
      const it = {
        id: `act-${a.id || i}`, kind,
        title: a.outcome || KIND_META[t]?.label || "Activity",
        subtitle: a.rep ? `by ${a.rep}` : null, body: a.notes || "",
        date: a.activity_date || a.created_at,
      };
      if (kind === "email") {
        it.subject = a.subject || (a.outcome || "").replace(/^Emailed:\s*/i, "").trim() || "(no subject)";
        it.bodyHtml = a.body_html || null;
        it.owner = a.rep || null;
        it.direction = a.direction || null;
        it.captured = a.captured_from === "outlook"
          || a.source === "reply_capture" || a.source === "sent_capture";
        it.attachments = a.attachments || [];
        it.fromEmail = a.from_email || null;
        it.toEmail = a.to_email || null;
        it.threadKey = a.thread_id || norm(it.subject) || "email";
      }
      items.push(it);
    });
    meetings.forEach((m, i) => items.push({
      id: `mtg-${m.id || i}`, kind: "meeting",
      title: m.title || "Meeting", subtitle: m.attendees || null,
      body: m.summary || m.raw_notes || "", date: m.meeting_date || m.created_at,
    }));
    checks.forEach((h, i) => items.push({
      id: `hc-${h.id || i}`, kind: "health",
      title: `Health check — ${h.sentiment || ""} (${h.health_score ?? "?"}/10)`,
      subtitle: h.staff_name ? `by ${h.staff_name}` : null, body: h.notes || "",
      date: h.check_date,
    }));
    // upcoming task (prospect next action / client next check)
    if (isProspect && rec?.next_action_date) {
      items.push({
        id: "next-action", kind: "task", title: rec.next_action_type || "Follow up",
        subtitle: "Upcoming", body: rec.next_action_note || "", date: rec.next_action_date, upcoming: true,
      });
    }
    if (!isProspect && rec?.next_check_date) {
      items.push({ id: "next-check", kind: "task", title: "Health check due", subtitle: "Upcoming", date: rec.next_check_date, upcoming: true });
    }
    items.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    // collapse email items sharing a thread key into a single thread entry, and
    // every event for one quote into a single entry (Clients directive §28.4)
    const quoteRef = (it) => {
      const m = `${it.title} ${it.body}`.match(/\b(EQ-[\w-]+|\d{8}-\d{6,})\b/);
      return m ? m[1] : null;
    };
    const threads = new Map();
    const quotes = new Map();
    const out = [];
    for (const it of items) {
      const qref = it.kind === "note" ? quoteRef(it) : null;
      if (it.kind === "email") {
        if (threads.has(it.threadKey)) threads.get(it.threadKey).messages.push(it);
        else { const th = { ...it, isThread: true, messages: [it] }; threads.set(it.threadKey, th); out.push(th); }
      } else if (qref) {
        if (quotes.has(qref)) quotes.get(qref).events.push(it);
        else { const qe = { ...it, quoteRef: qref, events: [it] }; quotes.set(qref, qe); out.push(qe); }
      } else out.push(it);
    }
    for (const qe of quotes.values()) {
      qe.events.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      qe.date = qe.events[0].date;
      const verbs = [...new Set(qe.events.map((e) => (e.title || "").replace(/^quote\s*/i, "").trim().toLowerCase()).filter(Boolean))];
      qe.title = `Quote ${qe.quoteRef}${verbs.length ? ` · ${verbs.join(", ")}` : ""}`;
      qe.body = qe.events.length > 1 ? `${qe.events.length} events on this quote` : qe.events[0].body;
      qe.count = qe.events.length;
    }
    for (const th of threads.values()) {
      th.messages.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      th.date = th.messages[0].date;
      th.title = th.messages[0].title;
      th.count = th.messages.length;
    }
    out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return out;
  }, [activities, meetings, checks, rec, isProspect]);

  const filtered = filter === "all" ? timeline : timeline.filter((i) => i.kind === filter);
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = filtered.filter((i) => i.upcoming || (i.date && i.date > todayIso));
  const past = filtered.filter((i) => !(i.upcoming || (i.date && i.date > todayIso)));

  const copyLink = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); };

  if (loading) return <div className="flex items-center gap-2 text-[#8A857A] p-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading record…</div>;
  if (!rec) return <div className="p-6 text-[#8A857A]">Record not found.</div>;

  const name = isProspect ? rec.company_name : rec.company_name;
  const sub = isProspect ? [rec.suburb, rec.source].filter(Boolean).join(" · ") : rec.client_id;

  return (
    <div className="space-y-4" data-testid="record-view">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => navigate(isProspect ? "/?tab=sales_prospects" : "/?tab=cs_clients")}
          className="border-[#DAD5CA] bg-transparent text-[#59554D]" data-testid="record-back"
          onClickCapture={(e) => { e.preventDefault(); navigate(-1); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex-1" />
        {isProspect && rec.status !== "converted" && (
          <Button size="sm" onClick={() => setConvertOpen(true)}
            className="bg-[#0E6B3F] hover:bg-[#0b5733] text-white font-medium" data-testid="record-convert">
            Convert to Client
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={copyLink} className="border-[#DAD5CA] bg-transparent text-[#59554D]" data-testid="record-copy-link">
          <Link2 className="w-4 h-4 mr-1" /> Copy link
        </Button>
      </div>

      {isProspect && (
        <ConvertToClientDialog prospect={rec} open={convertOpen} staff={staff}
          onOpenChange={setConvertOpen}
          onDone={(res) => { if (res?.client_id) navigate(`/clients/${encodeURIComponent(res.client_id)}`); }} />
      )}

      <Dialog open={touchOpen} onOpenChange={setTouchOpen}>
        <DialogContent className="max-w-lg" data-testid="record-touch-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Log a Touch</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">Type</label>
                <Select value={touch.type} onValueChange={(v) => setTouch({ ...touch, type: v })}>
                  <SelectTrigger data-testid="record-touch-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(isProspect ? ACTIVITY_TYPES : ["note", "email", "call", "meeting"]).map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Date</label>
                <Input type="date" value={touch.activity_date} onChange={(e) => setTouch({ ...touch, activity_date: e.target.value })} data-testid="record-touch-date" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Rep</label>
                <Select value={touch.rep || "unassigned"} onValueChange={(v) => setTouch({ ...touch, rep: v === "unassigned" ? "" : v })}>
                  <SelectTrigger data-testid="record-touch-rep"><SelectValue placeholder="Rep" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">—</SelectItem>
                    {staff.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isProspect && (
                <div>
                  <label className="text-xs text-[#59554D]">Advance status to</label>
                  <Select value={touch.advance_status} onValueChange={(v) => setTouch({ ...touch, advance_status: v })}>
                    <SelectTrigger data-testid="record-touch-advance"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Leave unchanged</SelectItem>
                      {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {isProspect && touch.type === "call" && (
              <div className="grid grid-cols-2 gap-3 rounded-lg border border-[#DAD5CA] bg-[#F4F2ED] p-3">
                <div>
                  <label className="text-xs text-[#59554D]">Call outcome (auto-schedules next action)</label>
                  <Select value={touch.call_outcome} onValueChange={(v) => setTouch({ ...touch, call_outcome: v })}>
                    <SelectTrigger data-testid="record-call-outcome"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No auto-action —</SelectItem>
                      {CALL_OUTCOME_ORDER.map((c) => <SelectItem key={c} value={c}>{CALL_OUTCOME_LABELS[c]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {touch.call_outcome === "callback_requested" && (
                  <div>
                    <label className="text-xs text-[#59554D]">Callback date</label>
                    <Input type="date" value={touch.callback_date} onChange={(e) => setTouch({ ...touch, callback_date: e.target.value })} data-testid="record-callback-date" />
                  </div>
                )}
              </div>
            )}
            <Input value={touch.outcome} onChange={(e) => setTouch({ ...touch, outcome: e.target.value })}
              placeholder="Outcome (e.g. left voicemail, positive reply)" data-testid="record-touch-outcome" />
            <Textarea value={touch.notes} onChange={(e) => setTouch({ ...touch, notes: e.target.value })}
              placeholder="Notes" className="min-h-[70px]" data-testid="record-touch-notes" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTouchOpen(false)} disabled={loggingTouch} data-testid="record-touch-cancel">Cancel</Button>
            <Button className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" onClick={logTouch} disabled={loggingTouch} data-testid="record-touch-save">
              {loggingTouch && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Log Touch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-[30%_40%_30%] gap-4">
        {/* LEFT — identity & about */}
        <div className="space-y-4">
          <Card className="bg-white border-[#DAD5CA]">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#FFF7D1] flex items-center justify-center"><Building2 className="w-5 h-5 text-[#6B5600]" /></div>
                <div className="min-w-0">
                  <div className="text-lg font-semibold text-[#1A1A1A] truncate flex items-center gap-1.5" style={{ fontFamily: "Manrope, sans-serif" }}>
                    <span className="truncate">{name}</span>
                    <OverseasBadge currency={rec.currency} country={rec.country} testid="record-overseas" />
                  </div>
                  <div className="text-xs text-[#8A857A] truncate">{sub}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {isProspect && (
                  <Badge className="text-[10px] border-0 bg-[#F1EEE6] text-[#59554D]">{rec.status || "prospect"}</Badge>
                )}
                {isProspect && rec.priority_tier && <Badge variant="outline" className="text-[10px] border-[#DAD5CA] text-[#59554D]">{rec.priority_tier}</Badge>}
                {!isProspect && rec.is_vip && <Badge variant="outline" className="text-[10px] border-[#DAD5CA] text-[#59554D]">VIP</Badge>}
                {!isProspect && rec.has_sla && <Badge variant="outline" className="text-[10px] border-[#DAD5CA] text-[#59554D]">SLA</Badge>}
              </div>
              {!isProspect && (rec.products || []).length > 0 && (
                <div data-testid="record-products">
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#8A857A] font-semibold flex items-center gap-1 mb-1.5"><Package className="w-3 h-3" /> Products</span>
                  <div className="flex flex-wrap gap-1.5">
                    {(rec.products || []).map((p) => (
                      <span key={p} title={PRODUCT_LABEL[p] || p} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FFF7D1] text-[#6B5600] border border-[#F0E6B8]">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5 text-sm text-[#3A3630]">
                {rec.contact_name && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#8A857A]" /> {rec.contact_name}</div>}
                {rec.contact_email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#8A857A]" /> <a className="text-[#1D4ED8] hover:underline break-all" href={`mailto:${rec.contact_email}`}>{rec.contact_email}</a></div>}
                {rec.contact_phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#8A857A]" /> {rec.contact_phone}</div>}
                {rec.suburb && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#8A857A]" /> {rec.suburb}</div>}
                {rec.assigned_rep && <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-[#8A857A]" /> Rep: {rec.assigned_rep}</div>}
              </div>
            </CardContent>
          </Card>

          {!isProspect && (
            <RailCard
              title="Details"
              icon={FileText}
              action={!editingDetails ? (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[#8A6D00] hover:bg-[#FFF8D6]" onClick={openDetailsEdit} data-testid="record-details-edit">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              ) : (
                <div className="flex items-center gap-1">
                  <Button size="sm" className="h-7 px-2 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-xs" onClick={saveDetailsEdit} disabled={savingDetails} data-testid="record-details-save">
                    {savingDetails ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Save</>}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[#59554D]" onClick={() => setEditingDetails(false)} disabled={savingDetails} data-testid="record-details-cancel">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            >
              {!editingDetails ? (
                <dl className="space-y-2.5">
                  {[
                    ["Company", rec.company_name, false],
                    ["Client ID", rec.client_id, true],
                    ["JIRA organisation", rec.jira_org_name, true],
                    ["Last activity", rec.last_activity_date ? formatDateMed(rec.last_activity_date) : null, false],
                    ["Last check", rec.last_check_date ? formatDateMed(rec.last_check_date) : null, false],
                    ["Next due", rec.next_check_date ? formatDateMed(rec.next_check_date) : null, false],
                    ["Allocate version", rec.allocate_version, true],
                    ["Last system update", rec.allocate_last_update ? formatDateMed(rec.allocate_last_update) : null, false],
                  ].map(([k, v, mono]) => (
                    <div key={k} className="flex items-start justify-between gap-3">
                      <dt className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] pt-0.5 shrink-0">{k}</dt>
                      <dd className={`text-[13px] text-[#1A1A1A] text-right ${mono ? "mono" : ""}`}>{v || <span className="text-[#B5AFA3]">—</span>}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="space-y-3" data-testid="record-details-form">
                  <div>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] block mb-1">Company</label>
                    <Input value={detailsForm.company_name} onChange={(e) => setDetailsForm({ ...detailsForm, company_name: e.target.value })} className="h-8 text-sm" data-testid="record-details-company" />
                  </div>
                  <div>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] block mb-1">JIRA organisation</label>
                    <Input value={detailsForm.jira_org_name} onChange={(e) => setDetailsForm({ ...detailsForm, jira_org_name: e.target.value })} className="h-8 text-sm" data-testid="record-details-jira" />
                  </div>
                  <div>
                    <label className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] block mb-1.5">Products</label>
                    <div className="flex flex-wrap gap-1.5">
                      {PRODUCT_CODES.map((code) => {
                        const on = (detailsForm.products || []).includes(code);
                        return (
                          <button
                            key={code}
                            type="button"
                            title={PRODUCT_LABEL[code]}
                            onClick={() => setDetailsForm({ ...detailsForm, products: toggleProduct(detailsForm.products || [], code) })}
                            className={`text-[11px] font-semibold px-2 py-1 rounded-full border transition-colors ${on ? "bg-[#FFD108] border-[#E0B800] text-[#1A1A1A]" : "bg-white border-[#DAD5CA] text-[#8A857A] hover:border-[#B5AFA3]"}`}
                            data-testid={`record-product-${code}`}
                          >
                            {code}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] block mb-1">Allocate version</label>
                      <Input value={detailsForm.allocate_version} onChange={(e) => setDetailsForm({ ...detailsForm, allocate_version: e.target.value })} className="h-8 text-sm" data-testid="record-details-version" />
                    </div>
                    <div>
                      <label className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] block mb-1">Last update</label>
                      <Input value={detailsForm.allocate_last_update} onChange={(e) => setDetailsForm({ ...detailsForm, allocate_last_update: e.target.value })} className="h-8 text-sm" placeholder="YYYY-MM-DD" data-testid="record-details-lastupdate" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 pt-1">
                    {[["VIP", "is_vip"], ["SLA", "has_sla"], ["Active", "active"]].map(([lbl, key]) => (
                      <label key={key} className="flex items-center gap-2 text-[13px] text-[#1A1A1A]">
                        <Switch checked={detailsForm[key]} onCheckedChange={(v) => setDetailsForm({ ...detailsForm, [key]: v })} data-testid={`record-details-${key}`} />
                        {lbl}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </RailCard>
          )}

          {!isProspect && (
            <RailCard title="Runbook" icon={BookOpen}>
              {runbookLoading ? (
                <div className="flex items-center gap-2 text-[#8A857A] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : runbook && (runbook.server || runbook.remote_access_type || runbook.file_location || runbook.msp_provider?.name || runbook.last_reviewed_at) ? (
                <div className="space-y-2.5">
                  <dl className="space-y-2">
                    {[
                      ["Server", runbook.server, true],
                      ["Remote access", runbook.remote_access_type, false],
                      ["File location", runbook.file_location, true],
                      ["MSP provider", runbook.msp_provider?.name, false],
                      ["Last reviewed", runbook.last_reviewed_at ? formatDateMed(runbook.last_reviewed_at) : null, false],
                    ].map(([k, v, mono]) => (
                      <div key={k} className="flex items-start justify-between gap-3">
                        <dt className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#8A857A] pt-0.5 shrink-0">{k}</dt>
                        <dd className={`text-[13px] text-[#1A1A1A] text-right ${mono ? "mono" : ""}`}>{v || <span className="text-[#B5AFA3]">—</span>}</dd>
                      </div>
                    ))}
                  </dl>
                  <button onClick={() => navigate("/runbooks")} className="inline-flex items-center gap-1 text-sm font-semibold text-[#8A6D00] hover:underline" data-testid="record-runbook-full">
                    View full runbook <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <p className="text-[13px] text-[#8A857A]">No runbook found for this client.</p>
              )}
            </RailCard>
          )}

          {(rec.notes || rec.category) && (
            <RailCard title="About" icon={FileText}>
              <div className="space-y-2 text-sm text-[#3A3630]">
                {rec.category && <div><span className="text-xs text-[#8A857A] block">Category</span>{rec.category}</div>}
                {rec.notes && <div><span className="text-xs text-[#8A857A] block">Notes</span><span className="whitespace-pre-wrap">{rec.notes}</span></div>}
              </div>
            </RailCard>
          )}
        </div>

        {/* CENTRE — timeline */}
        <div className="space-y-3">
          {!isProspect && <NeedsAttention clientId={rec.client_id || recordId} refreshKey={activities.length + checks.length} />}
          <div className="flex items-center gap-1.5 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`h-8 px-3 rounded-full text-[11px] font-semibold transition-colors ${filter === f.key ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
                data-testid={`tl-filter-${f.key}`}>{f.label}</button>
            ))}
            <Button size="sm" className="h-8 ml-auto bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-xs" onClick={() => setTouchOpen(true)} data-testid="record-log-touch">
              <Plus className="w-3.5 h-3.5 mr-1" /> Log a touch
            </Button>
            {!isProspect && (
              <Button size="sm" variant="outline" className="h-8 border-[#DAD5CA] bg-white text-[#59554D] hover:bg-[#F4F2ED] text-xs" onClick={() => navigate(`/clients/${encodeURIComponent(rec.client_id || recordId)}/email`)} data-testid="record-send-email">
                <Mail className="w-3.5 h-3.5 mr-1" /> Send email
              </Button>
            )}
            {!isProspect && (
              <Button size="sm" variant="outline" className="h-8 border-[#DAD5CA] bg-white text-[#59554D] hover:bg-[#F4F2ED] text-xs" onClick={() => setApprovalOpen(true)} data-testid="record-request-approval">
                <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Request approval
              </Button>
            )}
          </div>
          <Card className="bg-white border-[#DAD5CA]">
            <CardContent className="p-4">
              <style>{`.rv-email-body{font-size:13px;line-height:1.55;color:#1A1A1A;word-break:break-word;overflow-wrap:anywhere}.rv-email-body *{max-width:100%}.rv-email-body img{max-width:100%;height:auto}.rv-email-body a{color:#8A6D00}.rv-email-body p{margin:0 0 8px}.rv-email-body table{max-width:100%!important;width:auto!important}.rv-email-body td,.rv-email-body th{word-break:break-word;max-width:100%}`}</style>
              {filtered.length === 0 ? (
                <div className="text-sm text-[#8A857A] py-8 text-center">No activity yet.</div>
              ) : (
                <>
                  {upcoming.length > 0 && (
                    <>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-2">Upcoming</div>
                      {upcoming.map((it) => <TimelineItem key={it.id} it={it} />)}
                    </>
                  )}
                  {past.length > 0 && (
                    <>
                      {upcoming.length > 0 && <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-2 mt-2">History</div>}
                      {past.map((it) => <TimelineItem key={it.id} it={it} />)}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT — related records */}
        <div className="space-y-4">
          <ContactsEditor base={isProspect ? `${API}/sales/prospects/${recordId}` : `${API}/customer-success/clients/${encodeURIComponent(recordId)}`} />

          <RailCard title="Quotes" icon={Handshake}>
            <QuotesPanel linkType={isProspect ? "prospect" : "client"} linkId={recordId} linkName={rec.company_name} contacts={contacts} />
          </RailCard>

          {isProspect ? (
            <RailCard title="Lead stage" icon={RouteIcon}>
              <div className="text-sm text-[#1A1A1A] font-medium capitalize">{(rec.status || "prospect").replace(/_/g, " ")}</div>
              {rec.next_action_date && <div className="text-xs text-[#59554D] mt-1">Next: {rec.next_action_type || "follow up"} · {formatDateMed(rec.next_action_date)}</div>}
            </RailCard>
          ) : (
            <>
              <RailCard title="Journey" icon={RouteIcon}>
                <div className="text-sm text-[#1A1A1A]">{rec.journey_status || rec.status || "—"}</div>
                {rec.next_check_date && <div className="text-xs text-[#59554D] mt-1">Next check: {formatDateMed(rec.next_check_date)}</div>}
                {rec.health_score != null && <div className="text-xs text-[#59554D] mt-1">Health: {rec.health_score}/10</div>}
              </RailCard>
              <RailCard title="Tickets" icon={Ticket} count={rec.open_tickets ?? rec.tickets ?? undefined}>
                <div className="text-xs text-[#8A857A]">{(rec.open_tickets ?? rec.tickets) ? `${rec.open_tickets ?? rec.tickets} open ticket(s)` : "No open tickets."}</div>
              </RailCard>
            </>
          )}
        </div>
      </div>
      <RequestApprovalDialog open={approvalOpen} onOpenChange={setApprovalOpen} record={{
        suggested_title: `Approve something on ${rec?.company_name || "this client"}`,
        link_type: "client", link_id: rec?.client_id || recordId, link_name: rec?.company_name,
        link_path: `/clients/${encodeURIComponent(rec?.client_id || recordId)}`,
      }} />
    </div>
  );
}
